import { Chromosome } from '../types/algorithm';
import { calculateChromosomeFitness } from '../utils/fitnessCalculator';
import {
  initializePopulation,
  calculateRWP,
  rouletteWheelSelection,
  singlePointCrossover,
  mutate,
  findBestChromosome,
} from './geneticAlgorithm';

/**
 * SFLA-GA Hybrid (SFLAGA) implementation from scratch
 * Combines SFLA memeplex structure with GA operators
 */

/**
 * Divides population into memeplexes
 */
function createMemeplexes(
  population: Chromosome[],
  memeplexCount: number
): Chromosome[][] {
  const memeplexes: Chromosome[][] = Array(memeplexCount)
    .fill(null)
    .map(() => []);

  for (let i = 0; i < population.length; i++) {
    memeplexes[i % memeplexCount].push(population[i]);
  }

  return memeplexes;
}

/**
 * Shuffles memeplexes back into population
 */
function shuffleMemeplexes(
  memeplexes: Chromosome[][],
  population: Chromosome[]
): void {
  let idx = 0;
  for (const memeplex of memeplexes) {
    for (const frog of memeplex) {
      population[idx++] = frog;
    }
  }
}

/**
 * Local search: improves worst frog using memeplex best (SFLA-inspired)
 * Optimized step size to prevent excessive gene copying on large problems
 */
function localSearchWorstFrog(
  worst: Chromosome,
  best: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][]
): Chromosome {
  // Limit step size: for large problems, don't copy too many genes at once
  // Step size: min(5, taskCount/10) to max(taskCount, 1)
  const maxStep = Math.max(1, Math.min(5, Math.floor(taskCount / 10)));
  const minStep = 1;
  const step = Math.floor(Math.random() * (maxStep - minStep + 1)) + minStep;
  
  const newFrog: Chromosome = {
    x: [...worst.x],
    fitness: 0,
    rwp: 0,
  };

  // Move towards best frog (copy some genes)
  // Use Set to avoid copying same gene multiple times
  const copiedIndices = new Set<number>();
  for (let i = 0; i < step && copiedIndices.size < taskCount; i++) {
    let taskIdx: number;
    do {
      taskIdx = Math.floor(Math.random() * taskCount);
    } while (copiedIndices.has(taskIdx));
    
    copiedIndices.add(taskIdx);
    newFrog.x[taskIdx] = best.x[taskIdx];
  }

  calculateChromosomeFitness(newFrog, matrix, taskCount, serviceCount);
  return newFrog;
}

/**
 * Evolves a memeplex using GA operators with improved strategy
 */
function evolveMemeplex(
  memeplex: Chromosome[],
  globalBest: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  crossoverRate: number,
  mutationRate: number,
  generation: number,
  maxGenerations: number
): void {
  if (memeplex.length === 0) return;

  const memeplexSize = memeplex.length;
  const crossoverCount = Math.floor((memeplexSize * crossoverRate) / 100);
  const mutateCount = Math.floor((memeplexSize * mutationRate) / 100);

  // Sort memeplex once at the beginning (best to worst)
  memeplex.sort((a, b) => b.fitness - a.fitness);
  const memeplexBest = memeplex[0];
  const memeplexWorst = memeplex[memeplexSize - 1];

  // Calculate RWP for memeplex (needed for selection)
  calculateRWP(memeplex);

  // Local search on worst frog (SFLA-inspired improvement)
  // This helps memeplex converge faster
  if (memeplexSize > 2) {
    let improved = localSearchWorstFrog(
      memeplexWorst,
      memeplexBest,
      taskCount,
      serviceCount,
      matrix
    );

    if (improved.fitness > memeplexWorst.fitness) {
      memeplex[memeplexSize - 1] = improved;
      // Re-sort after improvement
      memeplex.sort((a, b) => b.fitness - a.fitness);
    } else {
      // Try with global best if memeplex best didn't help
      improved = localSearchWorstFrog(
        memeplexWorst,
        globalBest,
        taskCount,
        serviceCount,
        matrix
      );
      if (improved.fitness > memeplexWorst.fitness) {
        memeplex[memeplexSize - 1] = improved;
        memeplex.sort((a, b) => b.fitness - a.fitness);
      }
    }
  }

  // Crossover operations with improved strategy
  // Use global best more frequently (50% instead of 33%)
  const children: Chromosome[] = [];
  
  for (let i = 0; i < crossoverCount; i++) {
    let parent1: Chromosome;
    let parent2: Chromosome;

    // Use global best as one parent 50% of the time (improved from 33%)
    // This improves convergence towards global optimum
    if (i % 2 === 0 && memeplex.length > 0) {
      parent1 = globalBest;
      parent2 = memeplex[rouletteWheelSelection(memeplex)];
    } else {
      parent1 = memeplex[rouletteWheelSelection(memeplex)];
      parent2 = memeplex[rouletteWheelSelection(memeplex)];
    }

    // Use both children from crossover (don't waste child2)
    const [child1, child2] = singlePointCrossover(parent1, parent2, taskCount);
    calculateChromosomeFitness(child1, matrix, taskCount, serviceCount);
    calculateChromosomeFitness(child2, matrix, taskCount, serviceCount);

    children.push(child1, child2);
  }

  // Replace worst individuals with best children (greedy replacement)
  // Sort children by fitness (best first)
  children.sort((a, b) => b.fitness - a.fitness);
  
  // Replace worst memeplex members with best children
  let childIdx = 0;
  let memeplexIdx = memeplexSize - 1;
  while (childIdx < children.length && memeplexIdx >= 0) {
    if (children[childIdx].fitness > memeplex[memeplexIdx].fitness) {
      memeplex[memeplexIdx] = children[childIdx];
      memeplexIdx--;
    }
    childIdx++;
  }

  // Re-sort memeplex after replacements
  memeplex.sort((a, b) => b.fitness - a.fitness);

  // Mutation operations with adaptive strategy
  // Early generations: more exploration, later: more exploitation
  const explorationRate = 1.0 - (generation / maxGenerations) * 0.5; // 1.0 -> 0.5

  for (let i = 0; i < mutateCount; i++) {
    // Prefer mutating worse individuals (but not always)
    let idx: number;
    if (Math.random() < 0.7) {
      // 70% chance to mutate worst half
      const worstHalfStart = Math.floor(memeplexSize / 2);
      idx = worstHalfStart + Math.floor(Math.random() * (memeplexSize - worstHalfStart));
    } else {
      idx = Math.floor(Math.random() * memeplexSize);
    }

    const mutated = mutate(memeplex[idx], taskCount, serviceCount);
    calculateChromosomeFitness(mutated, matrix, taskCount, serviceCount);

    // Accept mutation if better, or with probability based on exploration rate
    if (mutated.fitness > memeplex[idx].fitness || 
        (Math.random() < explorationRate && mutated.fitness >= memeplex[idx].fitness * 0.95)) {
      memeplex[idx] = mutated;
    }
  }

  // Final sort to maintain order
  memeplex.sort((a, b) => b.fitness - a.fitness);
}

/**
 * Runs SFLAGA for specified generations
 */
export async function runSFLAGA(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    memeplexCount: number;
    crossoverRate: number;
    mutationRate: number;
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void
): Promise<Chromosome> {
  // Initialize population
  let population = initializePopulation(
    parameters.populationSize,
    taskCount,
    serviceCount
  );

  // Evaluate initial population
  for (const chrom of population) {
    calculateChromosomeFitness(chrom, matrix, taskCount, serviceCount);
  }

  // Create memeplexes
  let memeplexes = createMemeplexes(population, parameters.memeplexCount);

  // Evolution loop
  for (let gen = 0; gen < parameters.generations; gen++) {
    // Find global best
    const globalBest = findBestChromosome(population);

    // Evolve each memeplex using GA operators with improved strategy
    for (const memeplex of memeplexes) {
      evolveMemeplex(
        memeplex,
        globalBest,
        taskCount,
        serviceCount,
        matrix,
        parameters.crossoverRate,
        parameters.mutationRate,
        gen,
        parameters.generations
      );
    }

    // Shuffle memeplexes (redistribute to maintain diversity)
    shuffleMemeplexes(memeplexes, population);

    // Recreate memeplexes for next generation
    memeplexes = createMemeplexes(population, parameters.memeplexCount);

    // Report progress
    const best = findBestChromosome(population);
    if (onGenerationComplete) {
      onGenerationComplete(gen + 1, best.fitness);
    }

    // Yield control to allow UI updates
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  return findBestChromosome(population);
}

